import { useState, FormEvent } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setStatus('success');
      setMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('website_alt');
      }

      const res = await fetch('https://readdy.ai/api/form/d9tf2ht3pcjqs2fcscqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('Thank you for subscribing! Check your inbox for the latest Basetao guides.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        setStatus('error');
        setMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section id="newsletter" className="py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-12">
          <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-5">
            <i className="ri-mail-send-line w-7 h-7 flex items-center justify-center text-accent-700"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Stay Updated With Basetao Insights
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-lg mx-auto">
            Get the latest Basetao reviews, coupon codes, shipping tips, and spreadsheet guides delivered straight to your inbox. No spam, unsubscribe anytime.
          </p>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {message && (
            <p className={`mt-4 text-sm ${status === 'success' ? 'text-secondary-700' : 'text-primary-700'}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}